import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ListFilters, SalesCreditNote, SalesCreditNoteService } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import { Observable, Subscription } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { BillingErpUrlService } from '../billing-erp-url.service';
import { downloadFile, viewFile } from '../shared/file';
import { SalesCreditNoteListDatasource } from './sales-credit-note-list-datasource';

@Component({
  selector: 'billing-ui-sales-credit-note-list',
  templateUrl: './sales-credit-note-list.component.html',
  styleUrls: ['./sales-credit-note-list.component.scss', '../shared/shared-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SalesCreditNoteListDatasource],
  standalone: false,
})
export class SalesCreditNoteListComponent implements OnInit, OnDestroy {
  @ViewChild('downloadAnchor', { static: true }) private downloadAnchor: ElementRef;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Filters for Looking up the Sales Credit Notes
  @Input() filters: Observable<ListFilters>;
  @Input() displayColumns: string[];
  @Input() displayForAdmin? = false;

  allColumns = [
    'creditNoteNumber',
    'externalId',
    'account',
    'creditNoteId',
    'creditNoteDate',
    'dueDate',
    'paymentStatus',
    'invoiceNumber',
    'creditNoteReason',
    'outstanding',
    'total',
    'actions',
  ];
  allColumnsNames = new Map([
    ['creditNoteNumber', 'Credit Note Number'],
    ['externalId', 'External ID'],
    ['account', 'Account'],
    ['creditNoteId', 'Credit Note ID'],
    ['creditNoteDate', 'Credit Note Date'],
    ['dueDate', 'Due Date'],
    ['paymentStatus', 'Payment Status'],
    ['invoiceNumber', 'Invoice Number'],
    ['creditNoteReason', 'Credit Note Reason'],
    ['outstanding', 'Outstanding'],
    ['total', 'Total'],
    ['actions', 'Actions'],
  ]);

  // DisplayColumnsNames are the mapped name values of the displayColumns
  displayColumnsNames: string[];
  private readonly creditNotePDFMap = new ObservableWorkStateMap<string, Blob>();
  private subscriptions: Subscription[] = [];

  constructor(
    private salesCreditNoteService: SalesCreditNoteService,
    public dataSource: SalesCreditNoteListDatasource,
    public dialog: MatDialog,
    public billingErpUrlService: BillingErpUrlService,
    private alertService: SnackbarService,
  ) {}

  download(salesCreditNote: SalesCreditNote): void {
    const id = salesCreditNote.creditNoteId;

    const retrievePdf$ = this.salesCreditNoteService.downloadPdf(id, salesCreditNote.merchantId);
    retrievePdf$
      .pipe(
        take(1),
        tap((file) => downloadFile(this.downloadAnchor, file, salesCreditNote.creditNoteNumber, 'application/pdf')),
        catchError((err) => {
          this.alertService.openErrorSnack('Failed to download credit note PDF');
          throw err;
        }),
      )
      .subscribe();
    this.creditNotePDFMap.startWork(id, retrievePdf$);
  }

  view(salesCreditNote: SalesCreditNote): void {
    const id = salesCreditNote.creditNoteId;
    const retrievePdf$ = this.salesCreditNoteService.downloadPdf(id, salesCreditNote.merchantId);
    retrievePdf$
      .pipe(
        take(1),
        tap((file) => viewFile(file, 'application/pdf')),
        catchError((err) => {
          this.alertService.openErrorSnack('Failed to get credit note PDF');
          throw err;
        }),
      )
      .subscribe();
    this.creditNotePDFMap.startWork(id, retrievePdf$);
  }

  isRowWorking$(id: string): Observable<boolean> {
    return this.creditNotePDFMap.isLoading$(id);
  }

  ngOnInit(): void {
    this.dataSource.initialize(this.filters, this.paginator);
    this.displayColumns =
      this.displayColumns && this.displayColumns.length !== 0 ? this.displayColumns : this.allColumns;
    this.displayColumnsNames = this.displayColumns.map((column) => this.allColumnsNames.get(column));
  }

  public getSalesCreditNoteDetails(creditNoteId: string): string {
    return this.billingErpUrlService.getSalesCreditNoteDetails(creditNoteId);
  }

  ngOnDestroy(): void {
    this.subscriptions.map((s) => s.unsubscribe());
  }
}
